import { useCallback, useEffect, useMemo, useState } from 'react';

import { getDemoConfig } from './config';
import {
    Document,
    DocumentProgress,
    ExtractedData,
    ProcessStage,
} from './types';

export interface UseDemoPipelineSimulationProps {
    fileName?: string;
    fileSize?: string;
}

export interface UseDemoPipelineSimulationReturn {
    stage: ProcessStage;
    uploadProgress: number;
    documents: Document[];
    extractedData: ExtractedData[];
    documentProgress: Record<number, DocumentProgress>;
    sortedExtractedData: ExtractedData[];
    isCol1Active: boolean;
    isCol2Active: boolean;
    isCol3Active: boolean;
}

/**
 * Custom hook that orchestrates the document ingestion pipeline demo simulation.
 * Manages all state and timing for the upload → splitting → extraction flow.
 */
export function useDemoPipelineSimulation({
    fileName,
    fileSize,
}: UseDemoPipelineSimulationProps = {}): UseDemoPipelineSimulationReturn {
    // Memoize config to prevent infinite re-renders
    const DEMO_CONFIG = useMemo(
        () => getDemoConfig(fileName, fileSize),
        [fileName, fileSize],
    );
    const [stage, setStage] = useState<ProcessStage>('idle');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [extractedData, setExtractedData] = useState<ExtractedData[]>([]);
    const [documentProgress, setDocumentProgress] = useState<
        Record<number, DocumentProgress>
    >({});

    const isCol1Active = stage === 'uploading';
    const isCol2Active =
        (stage === 'splitting' || stage === 'extracting') &&
        !Object.values(documentProgress).every(
            (p) => p.splitting === 'complete',
        );
    const isCol3Active =
        (stage === 'extracting' || stage === 'splitting') &&
        Object.values(documentProgress).some((p) => p.extracting === 'loading');

    const checkIfComplete = useCallback(() => {
        setTimeout(() => {
            setDocumentProgress((prev) => {
                const allComplete = Object.values(prev).every(
                    (doc) => doc.extracting === 'complete',
                );
                if (allComplete) {
                    setStage('complete');
                }
                return prev;
            });
        }, 100);
    }, []);

    // Auto-start simulation on mount
    useEffect(() => {
        // Reset everything
        setStage('uploading');
        setUploadProgress(0);
        setDocuments([]);
        setExtractedData([]);
        setDocumentProgress({});

        const uploadInterval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(uploadInterval);
                    setTimeout(() => {
                        setStage('splitting');

                        setDocuments(DEMO_CONFIG.documents);

                        const initialProgress: Record<
                            number,
                            DocumentProgress
                        > = {};
                        DEMO_CONFIG.documents.forEach((doc) => {
                            initialProgress[doc.id] = {
                                splitting: 'loading',
                                extracting: 'pending',
                            };
                        });
                        setDocumentProgress(initialProgress);

                        const processDocument = (
                            docId: number,
                            extractedDoc: ExtractedData,
                            splitDelay: number,
                            extractDelay: number,
                        ) => {
                            setTimeout(() => {
                                setDocumentProgress((prev) => ({
                                    ...prev,
                                    [docId]: {
                                        ...prev[docId],
                                        splitting: 'complete',
                                    },
                                }));

                                // Prevent duplicates
                                setExtractedData((prev) => {
                                    if (
                                        prev.some(
                                            (d) => d.id === extractedDoc.id,
                                        )
                                    )
                                        return prev;
                                    return [...prev, extractedDoc];
                                });

                                setDocumentProgress((prev) => ({
                                    ...prev,
                                    [docId]: {
                                        ...prev[docId],
                                        extracting: 'loading',
                                    },
                                }));

                                setTimeout(() => {
                                    setDocumentProgress((prev) => ({
                                        ...prev,
                                        [docId]: {
                                            ...prev[docId],
                                            extracting: 'complete',
                                        },
                                    }));
                                    checkIfComplete();
                                }, extractDelay);
                            }, splitDelay);
                        };

                        // Process each document using config
                        DEMO_CONFIG.documents.forEach((doc, index) => {
                            const extractedDoc =
                                DEMO_CONFIG.extractedData[index];
                            const timing = DEMO_CONFIG.timing.documents[index];
                            processDocument(
                                doc.id,
                                extractedDoc,
                                timing.splitDelay,
                                timing.extractDelay,
                            );
                        });

                        setTimeout(
                            () => setStage('extracting'),
                            DEMO_CONFIG.timing.stageTransitionDelay,
                        );
                    }, DEMO_CONFIG.timing.uploadCompleteDelay);
                    return 100;
                }
                return prev + DEMO_CONFIG.timing.uploadProgressStep;
            });
        }, DEMO_CONFIG.timing.uploadInterval);
    }, [checkIfComplete, DEMO_CONFIG]);

    // Sort extracted data by ID to ensure correct 1:1 mapping with document cards
    const sortedExtractedData = [...extractedData].sort((a, b) => a.id - b.id);

    return {
        stage,
        uploadProgress,
        documents,
        extractedData,
        documentProgress,
        sortedExtractedData,
        isCol1Active,
        isCol2Active,
        isCol3Active,
    };
}
